/** 이용권 스토어 도메인 엔티티 (2안 v2: 같은 웹, 라우트 분리) */

export enum StoreProductType {
  /** 환자(자녀)용 치료 이용권 */
  TREATMENT = "TREATMENT",
  /** 보호자용 앱 이용권 */
  GUARDIAN_APP = "GUARDIAN_APP",
}

export interface StoreProduct {
  productId: string;
  type: StoreProductType;
  /** 이용 기간(개월): 3 | 6 | 12 */
  months: number;
  name: string;
  description: string;
  /** 판매가 (KRW, 부가세 포함) — 할인 적용가 */
  price: number;
  /** 정가 — 할인 중이면 price 와 다르며 취소선으로 표기한다 (없으면 null) */
  originalPrice: number | null;
  vatIncluded: boolean;
  /** 전자상거래법상 '재화 등의 공급 방법/시기' 표기용 문구 */
}

/** 위젯(일반) 결제용 주문 */
export interface StoreOrder {
  orderId: string;
  orderName: string;
  /** 결제 금액 (KRW) — 서버가 상품 기준으로 산출한 값 (부가세 별도 상품은 공급가액 + 부가세) */
  amount: number;
  supplyAmount: number | null;
  vatAmount: number | null;
  currency: "KRW";
}

export interface StoreChild {
  patientUuid: string;
  name: string;
  /** YYYY-MM-DD */
  birthDate: string | null;
  /** 이미 진행 중인 치료가 있는지 여부 (중복 구매 방지 안내용) */
  hasActiveTreatment: boolean;
  /** 결제된 미사용 이용권 보유 — 추가 구매 차단 */
  hasUnusedVoucher: boolean;
}

/** 보호자 검색 결과 — 등록된 보호자를 이름+전화번호로 확인한다 (1안 방식 폴백) */
export interface StoreGuardian {
  guardianUuid: string;
  name: string;
  /** 마스킹된 전화번호 표시용 (예: 010-****-1234) */
  maskedPhone: string;
  children: StoreChild[];
}

/** '치료 추가' 진입 시 환자 컨텍스트 (환자·보호자 확인, 전화번호 마스킹) */
export interface StorePatientContext {
  guardianUuid: string;
  guardianName: string;
  guardianMaskedPhone: string;
  patientUuid: string;
  patientName: string;
  /** YYYY-MM-DD (없으면 null) */
  patientBirthDate: string | null;
  /** 결제된 미사용 이용권 보유 — 있으면 구매 대신 치료 등록으로 보낸다 */
  hasUnusedVoucher: boolean;
}

/**
 * 구매 결과 — 서버가 결제 승인 + 이용권 발급을 원자적으로 처리한 결과
 * (실패 시 결제 자동 취소). 치료 등록(시작일 선택)은 치료 등록 화면에서 진행하며,
 * 서버가 등록 시 미사용 이용권을 검증·소진한다.
 */
export interface StorePurchaseResult {
  orderId: string;
  voucherIssued: boolean;
  /** 서명 영수증 JWT — 의료 서버에 제출해 이용권 자격을 발급받는다 */
  receipt: string;
  receiptUrl: string | null;
  approvedAt: string | null;
}

/** 의료 서버가 발급한 치료 이용권 자격 */
export interface TreatmentVoucherClaim {
  voucherUuid: string;
  orderId: string;
  months: number;
  alreadyClaimed: boolean;
}

/** 관리자 정산: 활성화 기록 + 수수료 */
export interface StoreSettlementRecord {
  /** SALE = 판매, REFUND = 환불 차감(음수) */
  type: "SALE" | "REFUND";
  /** 결제 승인(또는 환불) 일시 (ISO, UTC) */
  activatedAt: string;
  /** 주문자 시간대 (IANA) — 표시 시 이 시간대로 변환 */
  timeZone: string;
  hospitalName: string;
  productName: string;
  months: number;
  amount: number;
  /** 파트너(예: CKD) 수수료 금액 */
  commission: number;
  partnerName: string;
  orderId: string;
}

export interface StoreSettlementPage {
  /** 조회 월 (YYYY-MM) */
  month: string;
  items: StoreSettlementRecord[];
  summary: {
    totalCount: number;
    refundCount: number;
    totalAmount: number;
    totalCommission: number;
  };
}

/** 결제 내역 */
export type StorePaymentStatus = "PAID" | "REFUNDED" | "CANCELED";
export type StorePaymentMethodType = "CARD" | "SAVED_CARD";

export interface StorePaymentHistoryRecord {
  orderId: string;
  /** 결제 일시 (ISO, UTC) */
  paidAt: string;
  /** 주문자 시간대 (IANA) — 표시 시 이 시간대로 변환 */
  timeZone: string;
  /** 마스킹된 환자 이름 (서버에서 마스킹하여 반환, 예: 김*녀) */
  patientNameMasked: string;
  productName: string;
  months: number;
  amount: number;
  method: StorePaymentMethodType;
  status: StorePaymentStatus;
  receiptUrl: string | null;
  /** 자격 발급 완료 시각 — null 이면 미수령 */
  claimedAt: string | null;
  refundedAt: string | null;
  refundAmount: number | null;
  refundType: string | null;
  /** 결제 완료 상태 — 실제 규칙·금액은 견적 단계에서 확정 */
  refundable: boolean;
  exceptionApproved: boolean;
  refundRequest: { id: number; status: string; reason: string; createdAt: string } | null;
}

/** 환불 견적 (증서 발급 후, 실행 전) */
export interface StoreRefundQuote {
  orderId: string;
  /** store 환불 API 에 제출할 증서 — 견적과 실행이 같은 잠금 안에서 이뤄진다 */
  attestation: string;
  amount: number;
  refundType: "WITHDRAWAL" | "PRORATA" | "EXCEPTION";
  totalDays: number | null;
  remainingDays: number | null;
  withdrawalDeadline: string | null;
}

export interface StoreRefundRequest {
  id: number;
  orderId: string;
  hospitalName: string;
  productName: string;
  patientNameMasked: string;
  amount: number;
  reason: string;
  detail: string;
  status: string;
  createdAt: string;
  decidedAt: string | null;
  decisionNote: string | null;
}

/** 환불 결과 */
export interface StoreRefundResult {
  orderId: string;
  refundedAt: string | null;
  amount: number;
  refundType: string | null;
  /** 의료 서버 자격 회수까지 끝났는지 — false 면 돈은 환불됐고 회수만 재시도 필요 */
  voucherRevoked: boolean;
}

/**
 * 결제 내역 페이지 응답 (서버 페이지네이션)
 * 서버가 로그인 계정의 소속 병원으로 범위를 제한해 반환한다.
 */
export interface StorePaymentHistoryPage {
  /** 로그인 계정 소속 병원명 */
  hospitalName: string;
  items: StorePaymentHistoryRecord[];
  page: number;
  totalPages: number;
  totalCount: number;
  /** 현재 필터 조건 기준 합계 (서버 계산) */
  summary: {
    paidCount: number;
    paidAmount: number;
    refundedCount: number;
    refundedAmount: number;
  };
}

/**
 * 결제 방식 — BRANDPAY: 간편결제(본인이 브랜드페이에 등록한 카드, 기본) / GENERAL: 일반결제(결제창에서 매번 카드 입력, 저장 없음)
 * 위젯 흐름(VITE_STORE_TOSS_FLOW=widget)이면 GENERAL 고정.
 */
export type StorePaymentFlow = "BRANDPAY" | "GENERAL";

/** 브랜드페이 고객 — 의료진 본인 1명당 customerKey 1개 (서버 발급 UUID) */
export interface StoreBrandpayCustomer {
  customerKey: string;
  flow: "brandpay" | "widget";
}

/** 본인이 브랜드페이에 등록한 카드 — 토스가 마스킹한 표시값만 */
export interface StoreBrandpayCard {
  methodKey: string;
  alias: string | null;
  cardName: string | null;
  cardNumber: string | null;
  cardType: string | null;
  ownerType: string | null;
}
